import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface AddCardComposerProps {
  onAdd: (name: string) => void;
}

export default function AddCardComposer({ onAdd }: AddCardComposerProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState("");
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open && ref.current) ref.current.focus();
  }, [open]);

  const submit = () => {
    const name = value.trim();
    if (!name) return;
    onAdd(name);
    setValue("");
    if (ref.current) ref.current.focus();
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1 flex w-full items-center gap-1.5 rounded-lg px-2 py-1.5 text-left text-[13px] max-md:min-h-11 font-medium text-[var(--p-color-text-secondary)] hover:bg-[var(--p-color-bg-surface-tertiary)] hover:text-[var(--p-color-text)]"
      >
        <span className="text-base leading-none">+</span>
        {t("project-create", { defaultValue: "Add a card" })}
      </button>
    );
  }

  return (
    <div className="mt-1">
      <textarea
        ref={ref}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            submit();
          }
          if (e.key === "Escape") {
            setOpen(false);
            setValue("");
          }
        }}
        rows={2}
        placeholder={t("title", { defaultValue: "Enter a title…" })}
        className="w-full resize-none rounded-lg border border-[var(--p-color-border)] px-2.5 py-2 text-[16px] shadow-sm lg:text-[13px] focus:border-[var(--p-color-border-focus)] focus:outline-none focus:ring-1 focus:ring-[var(--p-color-border-focus)]"
      />
      <div className="mt-1.5 flex items-center gap-2">
        <button
          type="button"
          onClick={submit}
          className="rounded-md bg-[var(--p-color-bg-fill-brand)] px-3 py-1.5 text-[13px] font-medium max-md:min-h-11 text-[var(--p-color-text-brand-on-bg-fill)] hover:bg-[var(--p-color-bg-fill-brand-hover)]"
        >
          {t("save", { defaultValue: "Add card" })}
        </button>
        <button
          type="button"
          onClick={() => {
            setOpen(false);
            setValue("");
          }}
          className="rounded-md px-2 py-1.5 text-[13px] max-md:min-h-11 max-md:min-w-11 text-[var(--p-color-text-secondary)] hover:bg-[var(--p-color-bg-surface-tertiary)]"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
